import clsx from 'clsx'
import type { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { View } from 'react-native'
import Animated from 'react-native-reanimated'

import { Card, CardHeader } from '@/src/elements/card'
import { Chip } from '@/src/elements/chip/Chip'
import { Icon } from '@/src/elements/media/Icon'
import { Pressable } from '@/src/elements/pressable'
import { GradientTypography } from '@/src/elements/text/gradient'
import { Typography } from '@/src/elements/text/Typography'
import type { TIntention } from '@/src/features/intention/models/Intention.types'
import { usePrettifyPrayers } from '@/src/features/intention/ui/hooks/usePrettifyPrayers'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'

import { useCardBirthdayAnimation } from './hooks/useAnimation'

export type THappyBirthdayCardProps = {
  className?: string
  username: string
  prayers: TIntention['prayers']
  onChipPress?: VoidFunction
}

export const HappyBirthdayCard = ({
  username = '',
  prayers = [],
  onChipPress,
  className,
}: THappyBirthdayCardProps): ReactNode => {
  const { setShowCard, showCard, prayTextStyle, psalmTextStyle } = useCardBirthdayAnimation()
  const { others } = usePrettifyPrayers(prayers)
  return (
    <Card className={clsx(className, 'w-full items-center bg-primary-bg-light md:w-[343px]')}>
      <CardHeader className="flex flex-row justify-between">
        <View className="flex-1" />
        <View className="flex-1 pt-4">
          <Icon name={ICON.BIRTHDAY} size="xl" className="text-primary-page-title" />
        </View>
        <Pressable testID={TEST_ID.MISCELLANEOUS.TOGGLE_BIRTHDAY_CARD} onPress={() => setShowCard((p) => !p)}>
          <Icon
            name={ICON.CHEVRON_DOWN}
            className={clsx(
              {
                'rotate-90 transform-gpu': showCard,
                'rotate-0 transform-gpu': !showCard,
              },
              'text-primary-label-light transition duration-300 ease-in-out',
            )}
          />
        </Pressable>
      </CardHeader>
      <View className="min-h-14 native:h-14 min-w-full pb-2">
        <GradientTypography
          multicolor
          variant="gradient"
          contrast="high"
          bold
          size="lg"
          className="text-center font-black"
        >
          <FormattedMessage id="user.happy-birthday.title" values={{ username }} />
        </GradientTypography>
      </View>
      <Animated.View className="justify-center" style={prayTextStyle}>
        <Typography className="mx-4 text-center" contrast="default" colors="primary">
          <FormattedMessage id="user.happy-birthday.users-pray-for-you" />
        </Typography>
      </Animated.View>
      <View className="mx-4 mb-4 mt-3 native:max-w-[50%]">
        {prayers.length > 0 && (
          <Chip
            onPress={onChipPress}
            iconName={ICON.FIRE}
            colors="primary"
            text={others}
            badgeInfo={prayers.length - others.split(',').length}
          />
        )}
      </View>
      <Animated.View style={psalmTextStyle}>
        <View className="!items-center">
          <Typography className="px-6 pb-3 pt-3 text-center md:max-w-[50%]" contrast="default" colors="primary">
            <FormattedMessage id="user.happy-birthday.psalm" />
          </Typography>
        </View>
        <Typography className="mb-4 text-center" contrast="low" colors="primary" size="xs" bold>
          <FormattedMessage id="user.happy-birthday.reference" />
        </Typography>
      </Animated.View>
    </Card>
  )
}
